import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import { apiClient } from '../api/client';
import IntakeDatePicker from './patientIntake/IntakeDatePicker';
import {
  patientDetailControlRowSx,
  patientDetailDateFieldSx,
  patientDetailLabelRowSx,
} from './patientIntake/intakeFormLayout';

type Props = {
  patientId: number;
  initialValue: string | null;
  readOnly?: boolean;
  onSaved?: (value: string | null) => void;
};

/** 患者情報画面：対応期日（変更時に自動保存） */
const PatientActionDueDateEditor: React.FC<Props> = ({
  patientId,
  initialValue,
  readOnly,
  onSaved,
}) => {
  const [value, setValue] = useState(initialValue?.trim() ? initialValue.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const skipSaveRef = useRef(true);
  const savedTimerRef = useRef<number | null>(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  useEffect(() => {
    setValue(initialValue?.trim() ? initialValue.slice(0, 10) : '');
    skipSaveRef.current = true;
  }, [initialValue, patientId]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    const iso = value.trim() || null;

    void (async () => {
      setSaving(true);
      setError('');
      setSavedFlash(false);
      try {
        await apiClient.patch(`/api/patients/${patientId}`, { action_due_date: iso });
        onSavedRef.current?.(iso);
        setSavedFlash(true);
        if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
        savedTimerRef.current = window.setTimeout(() => setSavedFlash(false), 2000);
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.data?.error) {
          setError(String(e.response.data.error));
        } else {
          setError('保存に失敗しました');
        }
      } finally {
        setSaving(false);
      }
    })();
  }, [value, readOnly, patientId]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={patientDetailLabelRowSx}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          対応期日
        </Typography>
        {saving && <CircularProgress size={12} />}
        {!saving && savedFlash && (
          <Typography variant="caption" color="success.main">
            保存しました
          </Typography>
        )}
      </Box>
      <Box sx={{ ...patientDetailControlRowSx, width: { xs: '100%', sm: 168 } }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
          <IntakeDatePicker
            value={value}
            onChange={setValue}
            clearable
            disabled={readOnly}
            id="patient-action-due-date"
            fieldSx={patientDetailDateFieldSx}
          />
        </LocalizationProvider>
      </Box>
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PatientActionDueDateEditor;
